import type { IUpdateLocationApiSettingsCmd } from "@/data/commands/location/UpdateLocationApiSettingsCmd";
import { Factory } from "@/data/dao/Factory";
import { LocationDao } from "@/data/dao/service/LocationDao";
import { SystemDao } from "@/data/dao/service/SystemDao";
import type { ILocationDetail } from "@/data/model/Location";
import type { IApiVersion } from "@/data/model/System";
import { Validation } from "@/data/util/Validation";

import { Select } from "@/portal/formControls/Select";
import { Switch } from "@/portal/formControls/Switch";
import { BaseEntityForm } from "@/portal/forms/BaseEntityForm";

export class LocationApiSettingsForm extends BaseEntityForm<ILocationDetail, IUpdateLocationApiSettingsCmd> {
    private activeSwitch: Switch;
    private debugModeSwitch: Switch;
    private $userField: JQuery;
    private $passwordField: JQuery;
    private apiVersionSelect: Select<IApiVersion>;
    private needsPassword = false;

    constructor(element: JQuery) {
        super(element, {
            getSingle: undefined as any,
            create: undefined as any,
            getData: undefined as any,
            remove: undefined as any,
            update: (i, d) => Factory.getInstance<LocationDao>(LocationDao).setApiAccess(i, d),
        });

        this.activeSwitch = new Switch(this.$form.find('input[name="isactive"]'));
        this.activeSwitch.onChange = () => this.showHideActivatedFields();

        this.debugModeSwitch = new Switch(this.$form.find('input[name="isdebugmode"]'));
        this.$userField = this.$form.find('.form-control[name="user"]');
        this.$passwordField = this.$form.find('.form-control[name="password"]');
        this.apiVersionSelect = Select.createFromSelect(
            this.$form.find('.form-control[name="apiversion"]'),
            Factory.getInstance<SystemDao>(SystemDao).apiVersionDataSource,
            (c) => c.name + " - " + c.version,
            false
        );
    }

    public setFormData(model?: ILocationDetail) {
        this.activeSwitch.setValue(
            model === undefined || model.apiSettings === undefined ? false : model.apiSettings.isActive
        );
        this.debugModeSwitch.setValue(
            model === undefined || model.apiSettings === undefined ? false : model.apiSettings.isDebugMode
        );
        this.updateName(model === undefined || model.apiSettings === undefined ? "" : model.name + " API");
        this.$userField.val(model === undefined || model.apiSettings === undefined ? "" : model.apiSettings.user);
        this.$passwordField.val("");
        if (model === undefined || model.apiSettings === undefined) this.needsPassword = true;

        if (model !== undefined && model.apiSettings !== undefined)
            this.apiVersionSelect.setValue(model.apiSettings.apiVersionId);

        this.showHideActivatedFields();
    }

    private showHideActivatedFields() {
        if (this.activeSwitch.getValue())
            this.$form.find(".usersection, .packagesection, .kt-separator, .debugtoggle").show();
        else this.$form.find(".usersection, .packagesection, .kt-separator, .debugtoggle").hide();
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    public getFormData($form: JQuery): IUpdateLocationApiSettingsCmd {
        const isActive = this.activeSwitch.getValue();
        const data = {
            isActive: isActive,
            isDebugMode: isActive ? this.debugModeSwitch.getValue() : undefined,
            user: isActive ? (this.$userField.val().length > 0 ? this.$userField.val() : undefined) : undefined,
            password: isActive
                ? this.$passwordField.val().length > 0
                    ? this.$passwordField.val()
                    : undefined
                : undefined,
            apiVersionId: isActive ? this.apiVersionSelect.getValue() : undefined,
        };

        let hasError = false;

        if (isActive) {
            if (data.user === undefined) {
                hasError = true;
                this.$userField.addClass("is-invalid");
            }

            if (
                (this.needsPassword && (data.password === undefined || !Validation.isPassword(data.password))) ||
                (data.password !== undefined && !Validation.isPassword(data.password))
            ) {
                hasError = true;
                this.$passwordField.addClass("is-invalid");
            }

            if (data.apiVersionId === undefined) {
                hasError = true;
                this.apiVersionSelect.setError(true);
            } else this.apiVersionSelect.setError(false);
        }

        return hasError ? undefined : (data as any);
    }
}
